import { useState, useRef, useEffect } from 'react';
import Logo from './Logo';
import UserAvatar from './UserAvatar';
import { LogOutIcon, ImageIcon, FileTextIcon, CalendarIcon, EditIcon, CheckIcon, HistoryIcon, ArchiveIcon, ArrowLeftIcon, SettingsIcon, MoreIcon, TemplateIcon, UserPlusIcon } from './Icons';
import { exportPNG, exportPDF } from '../services/ExportService';
import { formatDate } from '../utils';

export default function SprintHeader({ sprint, onUpdateSprint, onLogout, userProfile, onShowHistory, onEndSprint, onShowProfile, project, onBackToProjects, onShowProjectSettings, isKanban, isRetro, onShowTemplates, onShowAddMembers }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [editingDates, setEditingDates] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const toInputDate = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toISOString().split('T')[0];
  };

  const handleEditName = () => {
    setName(sprint.name);
    setEditingName(true);
  };

  const handleSaveName = () => {
    if (name.trim()) {
      onUpdateSprint({ name: name.trim() });
    }
    setEditingName(false);
  };

  const handleEditDates = () => {
    setStartDate(toInputDate(sprint.startDate));
    setEndDate(toInputDate(sprint.endDate));
    setEditingDates(true);
  };

  const handleSaveDates = () => {
    if (startDate && endDate) {
      onUpdateSprint({
        startDate: new Date(startDate + 'T00:00:00'),
        endDate: new Date(endDate + 'T23:59:59'),
      });
    }
    setEditingDates(false);
  };

  const handleExportPNG = async () => {
    setExporting(true);
    try { await exportPNG(); } finally { setExporting(false); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try { await exportPDF(); } finally { setExporting(false); }
  };

  const menuAction = (fn) => {
    setShowMenu(false);
    fn();
  };

  return (
    <header className="sprint-header">
      <div className="sprint-header-left">
        {project && onBackToProjects && (
          <button className="btn btn-ghost btn-sm sprint-back-btn" onClick={onBackToProjects} title="Back to Projects">
            <ArrowLeftIcon size={16} />
          </button>
        )}
        <Logo size="small" showTagline />
        {project && (
          <span className="sprint-header-project-name">{project.name}</span>
        )}
      </div>

      {!isKanban && (
        <div className="sprint-header-center">
          <div className="sprint-name-row">
            {editingName ? (
              <div className="sprint-inline-edit">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                  autoFocus
                  className="sprint-name-input"
                />
                <button className="btn-icon" onClick={handleSaveName}><CheckIcon size={16} /></button>
              </div>
            ) : (
              <h2 className="sprint-name" onClick={handleEditName}>
                {sprint.name} <EditIcon size={14} />
              </h2>
            )}
          </div>

          <div className="sprint-dates-row">
            {editingDates ? (
              <div className="sprint-inline-edit">
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="sprint-date-input" />
                <span className="sprint-date-separator">to</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="sprint-date-input" />
                <button className="btn-icon" onClick={handleSaveDates}><CheckIcon size={16} /></button>
              </div>
            ) : (
              <span className="sprint-dates" onClick={handleEditDates}>
                <CalendarIcon size={14} />
                {formatDate(sprint.startDate)} &ndash; {formatDate(sprint.endDate)}
                <EditIcon size={12} />
              </span>
            )}
          </div>
        </div>
      )}

      <div className="sprint-header-right">
        <button className="btn btn-ghost btn-sm header-avatar-btn" onClick={onShowProfile} title="My Profile">
          <UserAvatar user={userProfile} size={32} showStatus={false} />
        </button>
        <div className="header-dropdown-wrap" ref={menuRef}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowMenu((v) => !v)} title="Menu">
            <MoreIcon size={18} />
          </button>
          {showMenu && (
            <div className="header-dropdown-menu">
              <button className="header-dropdown-item" onClick={() => menuAction(handleExportPNG)} disabled={exporting}>
                <ImageIcon size={15} /> Export as PNG
              </button>
              <button className="header-dropdown-item" onClick={() => menuAction(handleExportPDF)} disabled={exporting}>
                <FileTextIcon size={15} /> Export as PDF
              </button>
              <div className="header-dropdown-divider" />
              <button className="header-dropdown-item" onClick={() => menuAction(onShowHistory)}>
                <HistoryIcon size={15} /> {isRetro ? 'Retro History' : 'Sprint History'}
              </button>
              {onShowTemplates && (
                <button className="header-dropdown-item" onClick={() => menuAction(onShowTemplates)}>
                  <TemplateIcon size={15} /> Templates
                </button>
              )}
              {userProfile?.role === 'admin' && onShowProjectSettings && (
                <button className="header-dropdown-item" onClick={() => menuAction(onShowProjectSettings)}>
                  <SettingsIcon size={15} /> Project Settings
                </button>
              )}
              {userProfile?.role === 'admin' && onShowAddMembers && (
                <button className="header-dropdown-item" onClick={() => menuAction(onShowAddMembers)}>
                  <UserPlusIcon size={15} /> Add Members
                </button>
              )}
              <div className="header-dropdown-divider" />
              {!isKanban && (
                <button className="header-dropdown-item danger" onClick={() => menuAction(onEndSprint)}>
                  <ArchiveIcon size={15} /> {isRetro ? 'End Retro' : 'End Sprint'}
                </button>
              )}
              <button className="header-dropdown-item" onClick={() => menuAction(onLogout)}>
                <LogOutIcon size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
